import { useEditOrgUserLocationGroupsMutation } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import Action from '@/gf/components/Action'
import CloseModalButton from '@/gf/components/CloseModalButton'
import Modal from '@/gf/components/ModalNext'
import Field from '@/gf/components/next/forms/Field'
import TextInput from '@/gf/components/next/forms/TextInput'
import useMsgs from '@/gf/hooks/useMsgs'
import { ApolloError } from '@apollo/client'
import { useState } from 'react'
import type { LocationGroupUser } from './index'

const EditEmployeeIdModal = ({
  user,
  onClose,
  onSuccess,
}: {
  user: LocationGroupUser
  onClose: () => void
  onSuccess: () => void
}) => {
  const client = useGqlClient()
  const [_, msgr] = useMsgs()
  const [editOrgUser] = useEditOrgUserLocationGroupsMutation({ client })
  const [externalId, setExternalId] = useState(user.externalId || '')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (externalId.trim() === user.externalId) {
      onClose()
      return
    }

    setIsSubmitting(true)

    try {
      await editOrgUser({
        variables: {
          userId: user.id,
          externalId: externalId.trim(),
          name: null,
          phoneNumber: null,
          shippingLocationGroupIds: null,
          title: null,
        },
      })

      onSuccess()
    } catch (err) {
      const error = err as ApolloError
      const message = error.graphQLErrors.reduce((acc, error) => `${acc}${error.message}`, '')
      msgr.add(message || 'Failed to update employee ID', 'negative')
      setIsSubmitting(false)
    }
  }

  return (
    <Modal onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Edit Employee ID</h3>
            <CloseModalButton onClick={onClose} />
          </div>

          <div className="mb-4">
            <div className="text-sm text-gray-600 mb-1">{user.displayName}</div>
            <div className="text-xs text-gray-500">{user.email}</div>
          </div>

          <Field label="Employee ID" htmlFor="externalId">
            <TextInput
              id="externalId"
              value={externalId}
              setValue={setExternalId}
              placeholder="Employee ID"
              autoFocus
              required
            />
          </Field>

          <div className="mt-6 flex justify-end gap-3">
            <Action.S onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Action.S>
            <Action.P color="blue" type="submit" performing={isSubmitting} disabled={isSubmitting}>
              Save
            </Action.P>
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default EditEmployeeIdModal
